import React, { useState, useEffect } from 'react';
import { Layout, Table, Button, Form, Input, InputNumber, message, Popconfirm, Segmented } from 'antd';
import { PlusOutlined, UndoOutlined, BankTwoTone } from '@ant-design/icons';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import lineVotesService from '../../services/lineVotesService';
import '../../styles/finance.css';

const Finance = () => {
  const [activeTab, setActiveTab] = useState('line-votes');
  const [lineVotes, setLineVotes] = useState([]);
  const [deletedLineVotes, setDeletedLineVotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (activeTab === 'line-votes') {
      fetchLineVotes();
    } else if (activeTab === 'deleted-line-votes') {
      fetchDeletedLineVotes();
    }
  }, [activeTab]);

  const fetchLineVotes = async () => {
    try {
      setLoading(true);
      const response = await lineVotesService.getAll();
      if (response.success) {
        setLineVotes(response.lineVotes || []);
      }
    } catch (error) {
      message.error('Failed to fetch line votes');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values) => {
    try {
      setAdding(true);
      const response = await lineVotesService.add(
        values.departmentCode,
        values.lineVote,
        values.festival
      );
      if (response.success) {
        message.success('Line vote added successfully');
        form.resetFields();
        fetchLineVotes();
      }
    } catch (error) {
      message.error('Failed to add line vote');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await lineVotesService.delete(id);
      if (response.success) {
        message.success('Line vote deleted successfully');
        fetchLineVotes();
      }
    } catch (error) {
      message.error('Failed to delete line vote');
    }
  };

  const fetchDeletedLineVotes = async () => {
    try {
      setLoading(true);
      const response = await lineVotesService.getDeleted();
      if (response.success) {
        setDeletedLineVotes(response.deletedLineVotes || []);
      }
    } catch (error) {
      message.error('Failed to fetch deactivated line votes');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      const response = await lineVotesService.restore(id);
      if (response.success) {
        message.success('Line vote restored successfully');
        fetchDeletedLineVotes();
      }
    } catch (error) {
      message.error('Failed to restore line vote');
    }
  };

  const baseColumns = [
    {
      title: 'Festival',
      dataIndex: 'Festival',
      key: 'Festival',
      sorter: (a, b) => a.Festival.localeCompare(b.Festival),
      render: (v) => <span className="fin-festival">{v}</span>,
    },
    {
      title: 'Department code',
      dataIndex: 'departmentCode',
      key: 'departmentCode',
      width: 180,
      sorter: (a, b) => a.departmentCode - b.departmentCode,
      render: (v) => <span className="num">{v}</span>,
    },
    {
      title: 'Line vote',
      dataIndex: 'LineVote',
      key: 'LineVote',
      width: 160,
      sorter: (a, b) => a.LineVote - b.LineVote,
      render: (v) => <span className="fin-lv num">{v}</span>,
    },
  ];

  const columns = [
    ...baseColumns,
    {
      title: '',
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Deactivate line vote"
          description="It will no longer be offered on new direct orders. You can restore it later."
          onConfirm={() => handleDelete(record.id)}
          okText="Deactivate"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button danger size="small">Deactivate</Button>
        </Popconfirm>
      ),
    },
  ];

  const deletedColumns = [
    ...baseColumns,
    {
      title: '',
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Restore line vote"
          description="It will be offered on new direct orders again."
          onConfirm={() => handleRestore(record.id)}
          okText="Restore"
          cancelText="Cancel"
        >
          <Button icon={<UndoOutlined />} size="small">Restore</Button>
        </Popconfirm>
      ),
    },
  ];

  const showingActive = activeTab === 'line-votes';

  return (
    <DOTheme>
      <Layout className="do-shell do-scope fin-page">
        <NavigationBar />
        <main className="do-main fin-main">
          <section className="fin-sheet">
            <header className="fin-head">
              <div>
                <h1 className="fin-title">Line votes</h1>
                <p className="fin-sub">
                  {showingActive
                    ? 'Budget lines staff pick when raising a direct order.'
                    : 'Deactivated line votes stay on existing orders and can be restored.'}
                </p>
              </div>
              <Segmented
                value={activeTab}
                onChange={setActiveTab}
                options={[
                  { value: 'line-votes', label: 'Active' },
                  { value: 'deleted-line-votes', label: 'Deactivated' },
                ]}
                aria-label="Show"
              />
            </header>
            <Table
              className="fin-table"
              columns={showingActive ? columns : deletedColumns}
              dataSource={showingActive ? lineVotes : deletedLineVotes}
              loading={loading}
              rowKey="id"
              pagination={false}
              scroll={{ x: 640 }}
              locale={{ emptyText: showingActive ? 'No active line votes yet. Add one on the right.' : 'No deactivated line votes.' }}
            />
          </section>

          <aside className="fin-add" aria-labelledby="fin-add-title">
            <span className="fin-add__icon" aria-hidden="true"><BankTwoTone twoToneColor="#2748d9" /></span>
            <h2 className="fin-add__title" id="fin-add-title">Add a line vote</h2>
            <p className="fin-add__text">It becomes available on direct orders straight away.</p>
            <Form form={form} layout="vertical" onFinish={handleAdd} requiredMark={false}>
              <Form.Item
                name="festival"
                label="Festival"
                rules={[{ required: true, message: 'Please enter festival' }]}
              >
                <Input placeholder="e.g. Notte Bianca" />
              </Form.Item>
              <div className="fin-add__pair">
                <Form.Item
                  name="departmentCode"
                  label="Department code"
                  rules={[{ required: true, message: 'Please enter department code' }]}
                >
                  <InputNumber placeholder="e.g. 2415" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  name="lineVote"
                  label="Line vote"
                  rules={[{ required: true, message: 'Please enter line vote' }]}
                >
                  <InputNumber placeholder="e.g. 6251" style={{ width: '100%' }} />
                </Form.Item>
              </div>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={adding} block>
                Add line vote
              </Button>
            </Form>
          </aside>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default Finance;
